/**
 * Pear AppShell — React owns all rendering now.
 *
 * Replaces the hand-wired DOM in `index.html` + `main.ts`: showPair/showChat
 * become `status === 'pair'`, showTab becomes `tab` state (so the
 * `display:flex` vs `[hidden]` specificity bug that needed the
 * `[hidden] { display: none !important }` patch cannot occur), and the tab bar
 * carries one measured sliding indicator.
 *
 * Card WUHnArc6lNID — composition root. The shell's seams live under
 * web/src/shell/: dest.ts (routing vocabulary), tabs.tsx (rail tabs),
 * header.tsx (header), boot.ts + boot.tsx (auth/boot gate and its hooks),
 * listeners.ts (back / hash / visibility / date listeners), nav.ts
 * (navigateTo and the pick handlers), panels.tsx (the chat panels),
 * tabbar.tsx (the bottom rail) and overlays.tsx (the modal/toast host).
 * Pure move: every effect keeps its original declaration order and dependency
 * array — the hooks below are called in exactly the order App.tsx always
 * declared them, so boot order is unchanged.
 */
import { useCallback, useRef, useState } from 'react';
import { useSession } from './hooks/useSession';
import { useThread } from './hooks/useThread';
import { useKeyboardInset } from './hooks/useKeyboardInset';
/* UX item 5 (H5/H6): drafts, filters and scroll offsets survive a remount. */
import { useScrollMemory } from './hooks/useUiMemory.ts';
import { AppHeader } from './shell/header';
import {
  useFirstAct,
  useLastIdentity,
  usePendingShare,
  useReferralRedemption,
  useSessionActions,
  useThreadReset,
  useTokenSync,
} from './shell/boot';
import { BootGate } from './shell/gate';
import { ChatSection } from './shell/panels';
import { ShellTabBar } from './shell/tabbar';
import { OverlayHost } from './shell/overlays';
import {
  useDestPopstate,
  useHeaderDate,
  usePresence,
  useSettingsDeepLink,
  useSettingsHashClear,
} from './shell/listeners';
import { useAppNav } from './shell/nav';
/* Card psHEfEkK9cT2 — the one confetti layer, mounted beside the shell so a
   celebration survives a tab switch and no screen paints its own particles. */
import { ConfettiLayer } from './ui/Confetti';
import type { Tab } from './shell/dest';

/* The shell's public surface keeps its old import path — Thread and FirstAct
   import `Dest` / `ComposeIntent` from './App'. */
export { destHash, destFromHash } from './shell/dest';
export type { ComposeIntent, Dest } from './shell/dest';

export function App() {
  const [tab, setTab] = useState<Tab>('thread');
  // Token starts NULL (card yZexwSigXhw1): the session hook has not yet
  // checked that this device's couple token belongs to its own account, and
  // a socket opened before that check logs into the OLD room — which answered
  // bad_token and signed the person out of Pear entirely. The effect below
  // picks the token up one render later, once the session says 'chat'.
  const [token, setToken] = useState<string | null>(null);
  // Settings is a screen over the chat, not a sixth tab: opened from the
  // header, closed by Done or by picking a tab (card 0xhNPdS42FTh).
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Which Settings section shows (card DI31pLXCFc2C): pinned submenus, so the
  // selection lives here — a deep link (#settings-us) and a visit back both
  // keep it, exactly like lifeTab / usTab below.
  const [settingsTab, setSettingsTab] = useState('you');
  // More menu (card yOcdjnR6pXBz): the 5th slot in the bottom bar opens the
  // full menu — every screen and sub-view as a quick link, Settings inside.
  const [moreOpen, setMoreOpen] = useState(false);
  /* Collapsible desktop rail (card quWhYyrNiIbt, PRD L2) — collapses to ~72px
     icons-only and the pick is remembered per browser. Phones have no rail, so
     the state is inert there (the toggle is hidden and the CSS is gated to
     >=641px); no separate mobile branch needed. */
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    try { return window.localStorage.getItem('pear.rail.collapsed') === '1'; } catch { return false; }
  });
  const toggleRail = () => setRailCollapsed(prev => {
    const next = !prev;
    try { window.localStorage.setItem('pear.rail.collapsed', next ? '1' : '0'); } catch { /* private mode */ }
    return next;
  });
  // Quick-link requests the panels apply on mount/change (More menu picks).
  const [threadFilter, setThreadFilter] = useState<'all' | 'chat' | null>(null);
  const [notebookCollection, setNotebookCollection] = useState<string | null>(null);
  /* Card 5wr2rXdlCSTh: a Search result must open the item it names. The id is
     a one-shot request — Notebook opens that item's context panel and clears
     it, so a later visit does not reopen a stale card. */
  const [notebookItem, setNotebookItem] = useState<string | null>(null);
  // Life / Us sub-tabs (card sjFxIbWi3UgN). Held HERE rather than inside the
  // panels: both panels unmount on every bottom-bar switch, so local state
  // would drop you back on the first tab each visit — and the More menu's
  // quick links need to select the tab they are about to scroll to.
  // Life lands on Today, the consolidated dashboard (what needs you / what
  // is coming up); the compose deep-link below still selects the asks lane
  // deliberately when + → Ask is the entry point.
  const [lifeTab, setLifeTab] = useState('today');
  const [usTab, setUsTab] = useState('questions');
  /** Card GMat5z0Zxutf: every +1 opens the date-preferences segment after the
   *  More menu's "Date preferences" quick link lands on the Dates tab.
   *  Consumed, not latched (review 2026-10-19): the panel calls back once it
   *  has opened the overlay and we drop the signal to 0, so an ordinary
   *  remount of the Dates tab later in the session no longer reopens
   *  preferences on top of the user. */
  const [usPrefsSignal, setUsPrefsSignal] = useState(0);
  const clearPrefsSignal = useCallback(() => setUsPrefsSignal(0), []);
  // AI panel is a sibling to regular panels; opened from Settings > Agent.
  const [showingAI, setShowingAI] = useState(false);
  // Phase-0 screens over the tabs, opened from the More menu (card
  // YMMXiFOdFgdF): one state instead of a fifth tab, same shape as Settings.
  /* Help is the only screen that still takes the panel slot from the tabs:
     reveal history lives in Us, Questions and routing rules in Life, Inbox
     (cards IlGn5h3p3wDk, oQXsyU5lUDtV). */
  const [extraView, setExtraView] = useState<'help' | null>(null);
  // The concierge's walk-through (Jonathan email 2026-10-07): opens from
  // More → Take the tour and Help → Take the tour, never on its own any more
  // (card Tw2G4BUmOF5o).
  const [tourOpen, setTourOpen] = useState(false);
  const keyboardInset = useKeyboardInset();
  /* UX item 5 (H5/H6): exactly ONE .pear-panel is mounted at a time and the
     PANEL is what scrolls — .pear-main always fits its own column, so a ref on
     it would remember nothing. The ref follows whichever panel is up and the
     key names that screen, so Life, Us, Notebook, Settings and the extra views
     each come back where you left them. The Thread's stream scrolls inside
     .pear-thread and carries its own record. */
  const panelRef = useRef<HTMLElement | null>(null);
  const panelKey = extraView ? `extra:${extraView}` : settingsOpen ? 'settings' : showingAI ? 'ai' : tab;
  useScrollMemory(panelRef, `panel:${panelKey}`);
  const mountPanel = useCallback((el: HTMLDivElement | null) => {
    panelRef.current = el;
  }, []);

  const sessionRef = useRef<ReturnType<typeof useSession> | null>(null);
  const session = useSession(() => sessionRef.current?.dropCouple());
  sessionRef.current = session;
  /* Card BnJT98fFpzxU — the partner screen tells us when an auth form is open,
     so the landing beats can stand down while someone is signing in or
     verifying. On a phone they put the first field 3,920px down the page. */
  const [authOpen, setAuthOpen] = useState(false);

  const { lastIdentity, setLastIdentity, forgetIdentity } = useLastIdentity(session);

  const threadRef = useRef<ReturnType<typeof useThread> | null>(null);
  const thread = useThread({
    token,
    members: session.members,
    onInvalidToken: () => sessionRef.current?.dropCouple(),
  });
  threadRef.current = thread;

  /* The hooks below stand where App.tsx used to declare these effects
     inline, in the same order — effect order is boot behaviour. */
  useTokenSync(session, { setToken, setTab, setSettingsOpen, setExtraView });
  useThreadReset(token, threadRef);
  useSettingsDeepLink({ setSettingsTab, setShowingAI, setExtraView, setSettingsOpen });
  useDestPopstate({ setSettingsOpen, setShowingAI, setExtraView, setUsTab, setLifeTab, setTab });
  useSettingsHashClear(settingsOpen);
  const { firstActOpen, dismissFirstAct } = useFirstAct(session, thread);
  const nav = useAppNav({
    setTab,
    setSettingsOpen,
    setShowingAI,
    setExtraView,
    setUsTab,
    setLifeTab,
    setMoreOpen,
    setTourOpen,
    setUsPrefsSignal,
    setThreadFilter,
    setNotebookCollection,
  });
  const { pendingShare, consumePendingShare } = usePendingShare(session, nav.navigateTo);
  const { together, presenceHere, presenceText } = usePresence(session);
  const todayLabel = useHeaderDate();
  useReferralRedemption(session);
  const { leaveCouple, signOut, onOpenAI, onOpenHelp, onOpenTour } = useSessionActions({
    session,
    threadRef,
    setSettingsOpen,
    setShowingAI,
    setExtraView,
    setTourOpen,
    setLastIdentity,
  });

  const onChat = session.status === 'chat';

  return (
    <div
      id="app"
      className={`${onChat ? 'pear-app pear-has-nav' : 'pear-app is-landing'}${railCollapsed ? ' is-rail-collapsed' : ''}`}
      style={{ ['--kb-inset' as string]: `${keyboardInset}px` }}
      data-react-shell="app"
    >
      <AppHeader
        onChat={onChat}
        together={together}
        presenceHere={presenceHere}
        presenceText={presenceText}
        todayLabel={todayLabel}
      />

      <main className="pear-main">
        <BootGate
          onChat={onChat}
          session={session}
          authOpen={authOpen}
          setAuthOpen={setAuthOpen}
          lastIdentity={lastIdentity}
          forgetIdentity={forgetIdentity}
        />
        <ChatSection
          onChat={onChat}
          tab={tab}
          extraView={extraView}
          setExtraView={setExtraView}
          settingsOpen={settingsOpen}
          showingAI={showingAI}
          mountPanel={mountPanel}
          session={session}
          thread={thread}
          token={token}
          settingsTab={settingsTab}
          setSettingsTab={setSettingsTab}
          setSettingsOpen={setSettingsOpen}
          leaveCouple={leaveCouple}
          signOut={signOut}
          onOpenAI={onOpenAI}
          onOpenHelp={onOpenHelp}
          onOpenTour={onOpenTour}
          threadFilter={threadFilter}
          notebookCollection={notebookCollection}
          notebookItem={notebookItem}
          setNotebookItem={setNotebookItem}
          composeIntent={nav.composeIntent}
          pendingShare={pendingShare}
          consumePendingShare={consumePendingShare}
          openDatesFromNotebook={nav.openDatesFromNotebook}
          openUsFromNotebook={nav.openUsFromNotebook}
          usTab={usTab}
          setUsTab={setUsTab}
          usPrefsSignal={usPrefsSignal}
          clearPrefsSignal={clearPrefsSignal}
          addDateIdeaInNotebook={nav.addDateIdeaInNotebook}
          lifeTab={lifeTab}
          setLifeTab={setLifeTab}
          handleComposePick={nav.handleComposePick}
          clearComposeIntent={nav.clearComposeIntent}
          navigateTo={nav.navigateTo}
          firstActOpen={firstActOpen}
          dismissFirstAct={dismissFirstAct}
          goFirstAct={nav.goFirstAct}
          setTourOpen={setTourOpen}
        />
      </main>

      <ShellTabBar
        onChat={onChat}
        tab={tab}
        extraView={extraView}
        settingsOpen={settingsOpen}
        showingAI={showingAI}
        moreOpen={moreOpen}
        setMoreOpen={setMoreOpen}
        railCollapsed={railCollapsed}
        toggleRail={toggleRail}
        navigateTo={nav.navigateTo}
      />

      <OverlayHost
        onChat={onChat}
        session={session}
        moreOpen={moreOpen}
        setMoreOpen={setMoreOpen}
        handleMorePick={nav.handleMorePick}
        signOut={signOut}
        tourOpen={tourOpen}
        setTourOpen={setTourOpen}
      />

      {/* Card psHEfEkK9cT2 — one layer for every celebration, outside the
          panel tree so a burst completes even if the panel unmounts. */}
      <ConfettiLayer />
    </div>
  );
}
